import { Flex, Group, Modal, Stack, Text } from "@mantine/core";
import { type JSX } from "react";

import { ScheduleCallbackForm } from "@/call-controls";

import type { SessionSummaryViewProps } from "./sessionSummaryView";

import { CustomerDroppedNotification } from "./CustomerDroppedNotification";
import styles from "./EditSessionSummaryModal.module.css";
import { SessionSummaryActions } from "./SessionSummaryActions";
import { SessionSummaryForm } from "./SessionSummaryForm";

/** Prevent mantine's attempt to close the modal when the outside overlay is clicked */
const interceptClose = () => null;

/**
 * Dynamic callback session summary. Uses the same layout as the standard summary for now;
 * this is the component to change when the callback-oriented UI lands.
 */
export function DynamicSessionSummary({
    opened,
    showCallEndedBanner,
    view,
    summaryHeader,
    canCallback,
    partner,
    sessionSummaryLoading,
    shouldDisableTextArea,
    shouldShowTextArea,
    canSendSuccessfulSummary,
    buttonText,
    summaryUnavailable,
    callbacksRemaining,
    shouldCopy,
    expirySeconds,
    setView,
    onCopyOrEndSession,
    onScheduleCallback,
}: SessionSummaryViewProps): JSX.Element {
    return (
        <Group className={styles.modalContainer}>
            <Modal
                centered
                transitionProps={{
                    transition: "pop",
                }}
                classNames={{
                    inner: styles.modalInner,
                    overlay: styles.modalOverlay,
                    content: styles.modalContent,
                }}
                onClose={interceptClose}
                opened={opened}
                overlayProps={{
                    backgroundOpacity: 0.1,
                }}
                size="32rem"
                withCloseButton={false}
                title={showCallEndedBanner ? <CustomerDroppedNotification /> : null}
                styles={{
                    title: {
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flex: 1,
                        width: "100%",
                        minHeight: "none",
                    },
                    header: {
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flex: 1,
                        width: "100%",
                        minHeight: "none",
                        marginBottom: "var(--mantine-spacing-xs)",
                    },
                    body: {
                        padding: "var(--mb-padding, var(--mantine-spacing-md))",
                        overflowX: "hidden",
                        ...(showCallEndedBanner ? { paddingTop: 0 } : { paddingTop: "var(--mantine-spacing-md)" }),
                    },
                }}
            >
                <Flex id="modal-heading" align="center" justify="space-between" mb="xl">
                    <Stack gap={0}>
                        <Text className={styles.modalTitle} fw="var(--mantine-font-weight-normal)" fz="sm">
                            {view === "schedule-callback" ? "Schedule Callback" : summaryHeader}
                        </Text>
                        {!canCallback && (
                            <Text size="xs" c="warning.5">
                                You’ve reached the callback limit.
                            </Text>
                        )}
                    </Stack>
                </Flex>
                {view === "schedule-callback" ? (
                    <ScheduleCallbackForm callbackSurface="wrapUp" shouldShowActions={false} />
                ) : (
                    <SessionSummaryForm
                        partner={partner}
                        loading={sessionSummaryLoading}
                        shouldDisableTextArea={shouldDisableTextArea}
                        shouldShowTextArea={shouldShowTextArea}
                        canSendSuccessfulSummary={canSendSuccessfulSummary}
                    />
                )}
                <SessionSummaryActions
                    onBackClick={() => setView("edit-summary")}
                    afterSubmitClickDefault={() => void onScheduleCallback()}
                    getButtonText={() => buttonText}
                    setView={setView}
                    endSession={onCopyOrEndSession}
                    summaryUnavailable={summaryUnavailable}
                    callbackRemaining={callbacksRemaining}
                    shouldCopy={shouldCopy}
                    expirySeconds={expirySeconds}
                    view={view}
                />
            </Modal>
        </Group>
    );
}
